import type { JSX } from 'react';

// A blocking screen — the cloud equivalent of the desktop FTUE's MagicNoDevices
// / MagicAnalysisError beats (task 7.5, "NO SILENT COMPLETION"). When a scan
// finds nothing or an analysis fails — including when a relayed step times out
// or the boat is offline — the reveal stops here and demands an EXPLICIT user
// choice. It never auto-advances to the live view as if the boat were set up.
//
// Two shapes:
//   - SOFT stop (analysis_error / no_devices): retry AND a deliberate "continue
//     anyway" escape (`onContinue`).
//   - HARD stop (no_gateways, reveal-gateways-onetab D3): a REQUIREMENT screen —
//     Neomaris can't observe the boat without a gateway — so it offers ONLY a
//     rescan and NO escape (`onContinue` omitted), since the dashboard cannot
//     function without one.

export interface RevealBlockingProps {
  kind: 'no_devices' | 'analysis_error' | 'no_gateways';
  /** Extra detail (e.g. the analysis error, or "your boat is offline"). */
  detail?: string;
  onRetry: () => void;
  retryLabel: string;
  /**
   * The deliberate "I understand, move on anyway" escape. OMIT for a hard stop
   * (no_gateways) — there is no path forward without fixing the prerequisite, so
   * the continue button is not rendered.
   */
  onContinue?: () => void;
  continueLabel?: string;
  /** Retry is pointless right now (boat offline) — disable it + explain. */
  retryDisabled?: boolean;
  busy?: boolean;
  /**
   * A reassuring footnote that survives onto the blocking screen — e.g. the
   * concurrent "we still found N devices aboard" count, so an analysis hiccup
   * doesn't visually erase the device-finding progress (reveal-decision-flow
   * task 4c.5 / D16).
   */
  footnote?: string;
}

export function RevealBlocking({
  kind,
  detail,
  onRetry,
  retryLabel,
  onContinue,
  continueLabel,
  retryDisabled = false,
  busy = false,
  footnote,
}: RevealBlockingProps): JSX.Element {
  const title =
    kind === 'no_gateways'
      ? 'Neomaris needs a gateway to see your boat'
      : kind === 'no_devices'
        ? 'We didn’t find any devices yet'
        : 'We couldn’t finish the analysis';
  const body =
    kind === 'no_gateways'
      ? 'Neomaris listens to your boat through an NMEA gateway — without one, there’s nothing for us to read. Check that your gateway is powered on and connected to the same network as your Neomaris, then rescan.'
      : kind === 'no_devices'
        ? 'Your gateway is connected, but we haven’t heard any devices behind it yet. They’ll appear here as they report in.'
        : 'We reached your boat, but couldn’t complete the network analysis — this is usually a brief hiccup talking to our setup helper.';

  return (
    <div
      className={`reveal-stage reveal-block reveal-block--${kind}`}
      data-testid={`reveal-block-${kind}`}
    >
      <h3 className="reveal-stage__title">{title}</h3>
      <p className="reveal-stage__sub">{body}</p>
      {detail ? (
        <p className="reveal-block__note" role="note">
          {detail}
        </p>
      ) : null}
      {footnote ? (
        <p
          className="reveal-block__footnote"
          aria-live="polite"
          data-testid="reveal-block-footnote"
        >
          {footnote}
        </p>
      ) : null}
      <div className="reveal-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={onRetry}
          disabled={retryDisabled || busy}
          data-testid="reveal-block-retry"
        >
          {busy ? 'Trying…' : retryLabel}
        </button>
        {onContinue ? (
          <button
            type="button"
            className="btn btn-outline"
            onClick={onContinue}
            disabled={busy}
            data-testid="reveal-block-continue"
          >
            {continueLabel}
          </button>
        ) : null}
      </div>
    </div>
  );
}
